import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { PlanType } from "sdk/types";
import { routes, RoutesValues } from "~/constants";
import { useCurrentDiagnosticUser } from "~/hooks/useCurrentDiagnosticUser";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useQuery } from "~/hooks/useQuery";

interface Props {
  protectAuth?: boolean;
  protectConfirmation?: boolean;
  protectDiagnostics?: boolean;
  protectOnboarding?: boolean;
  protectPayment?: boolean;
  protectPaymentIssue?: boolean;
  protectSetPassword?: boolean;
  requireAuth?: boolean;
  requireDiagnostics?: boolean;
  requireGoodStanding?: boolean;
  requireNoPause?: boolean;
  requireOnboarding?: boolean;
  requirePayment?: boolean;
  requireSetPassword?: boolean;
  children: ReactNode;
}

const navigateTo = (route: RoutesValues, state: unknown): JSX.Element => {
  return <Navigate replace={true} state={state} to={route} />;
};

type User = ReturnType<typeof useCurrentUser>;
type DiagnosticUser = ReturnType<typeof useCurrentDiagnosticUser>;

const isDiagnosticRoute = (pathname: string): boolean => {
  return pathname.startsWith("/diagnostics");
};

interface NextRoute {
  route: RoutesValues | null;
  state?: unknown;
}

export const getNextRoute = (
  user: User,
  diagnosticUser: DiagnosticUser,
  pathname: string,
  {
    protectAuth,
    protectConfirmation,
    protectDiagnostics,
    protectOnboarding,
    protectPayment,
    protectPaymentIssue,
    protectSetPassword,
    requireAuth,
    requireDiagnostics,
    requireGoodStanding,
    requireNoPause,
    requireOnboarding,
    requirePayment,
    requireSetPassword,
  }: Omit<Props, "children">,
): NextRoute => {
  // Handle diagnostic routes with separate authentication
  if (isDiagnosticRoute(pathname)) {
    const isDiagnosticAuthenticated = !!diagnosticUser;

    // For diagnostic routes requiring auth, redirect to main sign in if not authenticated
    // (DiagnosticUser auth has been consolidated into User auth)
    if (requireDiagnostics && !isDiagnosticAuthenticated) {
      return { route: routes.signIn };
    }

    // For diagnostic routes that should protect against authenticated users
    if (protectDiagnostics && isDiagnosticAuthenticated) {
      return { route: routes.diagnosticsDashboard };
    }

    // Allow access to diagnostic route
    return { route: null };
  }

  const { externalId, isCoach, isOnboarded, isPasswordSet } = user ?? {};
  const status = user?.subscription?.status ?? null;
  const canceledAt = user?.subscription?.canceledAt ?? null;
  const partnerSlug = user?.partner?.slug ?? null;
  const partnerActivatedAt = user?.partner?.activatedAt ?? null;
  const partnerEndedAt = user?.partner?.endedAt ?? null;
  const isPartnerTrialEnded =
    !!partnerEndedAt && new Date(partnerEndedAt) < new Date();
  const isSubscriptionTrialing = status === "trialing";

  const hasActiveSubscription = !!status && status !== "incomplete";
  const hasActivatedPartnerTrial = !!partnerSlug && !!partnerActivatedAt;
  const isTrialOfferActive = hasActivatedPartnerTrial && !isPartnerTrialEnded;

  const hasPaid = hasActiveSubscription;
  const canExtend = isTrialOfferActive && isSubscriptionTrialing;
  const needsPartnerActivation =
    !hasActiveSubscription && partnerSlug && !partnerActivatedAt;
  const isFullyCanceled = status === "canceled" && !!canceledAt;
  const needsPartnerActivationPayload =
    partnerSlug && !partnerActivatedAt
      ? {
          route: routes.partnerActivate.replace(
            ":slug",
            partnerSlug,
          ) as RoutesValues,
        }
      : { route: routes.dashboard };
  const isOperational = status === "active" || status === "trialing";
  const isAuthenticated = !!externalId;
  const hasPaymentIssue = isAuthenticated && !isOperational && !isFullyCanceled;
  const isPaused = status == "paused";

  // This order is important. Users should be redirected in a way that mirrors the onboarding flow.
  if (requireAuth && !externalId) return { route: routes.signIn };
  if (requireOnboarding && !isOnboarded && !isCoach)
    return { route: routes.onboardingRoot };
  if (requirePayment && isFullyCanceled)
    return { route: routes.planSelect, state: { resubscribe: true } };
  if (requirePayment && needsPartnerActivation)
    return needsPartnerActivationPayload;
  if (requirePayment && !hasPaid) return { route: routes.planPayment };
  if (requireSetPassword && !isPasswordSet)
    return { route: routes.setPassword };
  if (requireNoPause && isPaused) return { route: routes.settings };
  if (requireGoodStanding && isPaused) return { route: routes.settings };
  if (requireGoodStanding && hasPaymentIssue)
    return { route: routes.paymentIssue };

  const defaultPage = routes.dashboard;

  if (protectAuth && externalId) return { route: defaultPage };
  if (protectConfirmation && hasPaid && !isFullyCanceled && !canExtend)
    return { route: defaultPage };
  if (protectOnboarding && isOnboarded) return { route: defaultPage };
  if (protectPayment && hasPaid && !isFullyCanceled && !canExtend)
    return { route: defaultPage };

  if (protectSetPassword && isPasswordSet) return { route: defaultPage };
  if (protectPaymentIssue && !hasPaymentIssue) return { route: defaultPage };

  return { route: null };
};

const types: PlanType[] = ["plus", "premium"];

export const Protector: FC<Props> = ({ children, ...props }) => {
  const user = useCurrentUser();
  const diagnosticUser = useCurrentDiagnosticUser();
  const location = useLocation();
  const query = useQuery();
  const type = query.get("type");

  const typeState = types.includes(type as PlanType) ? { type } : {};
  const locationState = { ...typeState, ...location.state };

  const { route, state } = getNextRoute(
    user,
    diagnosticUser,
    location.pathname,
    props,
  );
  const nextState = { ...locationState, ...(state ?? {}) };

  return route ? navigateTo(route, nextState) : <>{children}</>;
};
